<script>
import EgDropdown from "@/components/global/eg_dropdown.vue";
import EgToggleButton from "@/components/global/eg_toggle_button.vue";
import UppyDashboard from "@/components/dashboard/uppy_dashboard.vue";
import { getFreeToolsRubrics } from "@/api/requests/freeTools";

import { essayExample } from "./essayExample";

export default {
  name: "UploadEssayStep",
  components: { UppyDashboard, EgToggleButton, EgDropdown },
  props: {
    selectedEssayOption: {
      type: String,
      default: "",
    },
    essayContents: {
      type: String,
      default: "",
    },
    selectedGradingLevel: {
      type: String,
      default: "",
    },
    selectedGradingIntensity: {
      type: String,
      default: "",
    },
    files: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    "selectEssayOption",
    "setEssayContent",
    "selectGradingLevel",
    "selectRubric",
    "selectGradingIntensity",
    "grade",

    "file-added",
    "file-removed",
    "file-uploaded",
  ],
  data() {
    return {
      essayExample,
      registerLink: "https://app.essaygrader.ai/register",
      rubrics: [],
      selectedRubric: null,
    };
  },
  computed: {
    gradeIntensities() {
      return [
        { text: this.$t("freeTools.grade.intensityEasy"), value: "light" },
        { text: this.$t("freeTools.grade.intensityNormal"), value: "moderate" },
        { text: this.$t("freeTools.grade.intensityStrict"), value: "intense" },
      ];
    },
    essayOptions() {
      return [
        { text: this.$t("freeTools.grade.uploadFile"), value: "files" },
        { text: this.$t("freeTools.grade.enterText"), value: "text" },
      ];
    },
    gradeLevels() {
      return [
        {
          value: "elementary",
          text: this.$t("freeTools.grade.levelElementary"),
        },
        {
          value: "middleschool",
          text: this.$t("freeTools.grade.levelMiddleSchool"),
        },
        {
          value: "highschool",
          text: this.$t("freeTools.grade.levelHighSchool"),
        },
        { value: "college", text: this.$t("freeTools.grade.levelCollege") },
      ];
    },
  },
  watch: {
    selectedRubric(v) {
      this.$emit("selectRubric", v);
    },
  },
  async mounted() {
    await this.fetchRubrics();
  },

  methods: {
    async fetchRubrics() {
      const { data } = await getFreeToolsRubrics();
      this.rubrics = data;
      const selectedRubricId = process.env.VUE_APP_FREE_TOOLS_DEFAULT_RUBRIC_ID;
      this.selectedRubric =
        data.find((rubric) => rubric.id === Number(selectedRubricId)) ||
        data[0];
    },

    upload() {
      return this.$refs.uppyDashboard.upload();
    },
  },
};
</script>

<template>
  <div>
    <div class="step-1-layout mb-3">
      <div>
        <b>{{ $t("freeTools.grade.studentSubmission") }}</b>
        <div class="d-flex align-items-center flex-gap mt-2">
          <eg-b-button
            class="outline use-sample"
            @click="
              $emit('setEssayContent', essayExample);
              $emit('selectEssayOption', 'text');
            "
          >
            {{ $t("freeTools.grade.insertSampleEssay") }}
          </eg-b-button>
          <eg-b-button
            class="outline"
            @click="
              $emit(
                'selectEssayOption',
                selectedEssayOption === 'files' ? 'text' : 'files'
              );
              $emit('setEssayContent', '');
            "
          >
            {{ essayOptions[selectedEssayOption === "files" ? 1 : 0].text }}
          </eg-b-button>
        </div>
      </div>
      <div class="uppy-container">
        <uppy-dashboard
          v-if="selectedEssayOption === 'files'"
          ref="uppyDashboard"
          class="uppy"
          :path="'essays'"
          :value="files"
          plugin-name-camera-title="Handwriting"
          :accept-services="['googleDrive', 'oneDrive', 'dropbox']"
          :max-number-of-files="1"
          @input="$emit('file-uploaded', $event)"
          @file-added="$emit('file-added', $event)"
          @file-removed="$emit('file-removed', $event)"
        />
        <eg-b-form-textarea
          v-if="selectedEssayOption === 'text'"
          class="textarea"
          :value="essayContents"
          :placeholder="$t('freeTools.grade.enterSubmissionPlaceholder')"
          @update="$emit('setEssayContent', $event)"
        />
      </div>
      <div>
        <div>
          <b>{{ $t("freeTools.grade.customizeGrading") }}</b>
        </div>
        <div>
          {{ $t("freeTools.grade.customizeGradingDescription") }}
        </div>
      </div>

      <div>
        <div class="panel">
          <b>{{ $t("freeTools.grade.studentLevel") }}</b>
          <div class="grading-buttons-row mt-2">
            <eg-toggle-button
              v-for="level in gradeLevels"
              :key="level.value"
              :pressed="selectedGradingLevel === level.value"
              size="sm"
              @click="$emit('selectGradingLevel', level.value)"
            >
              {{ level.text }}
            </eg-toggle-button>
          </div>
          <div class="mb-3">
            <eg-dropdown>
              <template #button-content>
                <div class="d-flex align-items-center justify-content-between">
                  <div
                    v-if="selectedRubric"
                    class="d-flex flex-column align-items-start"
                  >
                    <span>{{ selectedRubric.name }}</span>
                    <span>
                      {{ $t("freeTools.grade.createdBy") }}
                      <img
                        src="@/assets/eg-logo-tiny-transparent.png"
                        alt="EssayGrader"
                        class="logo"
                      />
                    </span>
                  </div>
                  <div v-else class="d-flex flex-column align-items-start">
                    <span style="line-height: 3">{{
                      $t("freeTools.grade.selectRubricToGrade")
                    }}</span>
                  </div>
                  <eg-b-icon class="ml-auto" icon="chevron-down"></eg-b-icon>
                </div>
              </template>
              <eg-b-dropdown-item-button
                v-for="rubric in rubrics"
                :key="rubric.id"
                class=""
                @click="() => (selectedRubric = rubric)"
              >
                {{ rubric.name }}
                <br />
                {{ $t("freeTools.grade.createdBy") }}
                <img
                  src="@/assets/eg-logo-tiny-transparent.png"
                  alt="EssayGrader"
                  class="logo"
                />
              </eg-b-dropdown-item-button>
              <eg-b-dropdown-divider />
              <eg-b-button
                :href="registerLink"
                size="sm"
                class="outline mx-4 my-1"
                target="_blank"
              >
                {{ $t("freeTools.grade.createCustomRubric") }}
              </eg-b-button>
            </eg-dropdown>
          </div>
          <div>
            <b>{{ $t("freeTools.grade.gradingIntensity") }}</b>
            <div class="grading-buttons-row mt-2 pb-0">
              <eg-toggle-button
                v-for="level in gradeIntensities"
                :key="level.value"
                :pressed="selectedGradingIntensity === level.value"
                size="sm"
                @click="$emit('selectGradingIntensity', level.value)"
              >
                {{ level.text }}
              </eg-toggle-button>
            </div>
          </div>
        </div>
        <eg-b-button
          :disabled="
            (selectedEssayOption === 'files' && files.length === 0) ||
            (selectedEssayOption === 'text' && !essayContents)
          "
          class="button large mt-3 grade-button"
          @click="$emit('grade')"
        >
          {{ $t("freeTools.grade.gradeNow") }}
        </eg-b-button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.step-1-layout {
  display: grid;

  grid-template: auto auto / 1fr 1fr;
  grid-auto-flow: column;
  grid-gap: 1rem;
}

@media (max-width: 900px) {
  .step-1-layout {
    grid-template: auto 300px auto auto / auto;
  }
}

.uppy-container {
  container-type: size;
}

.uppy ::v-deep {
  .uppy-Dashboard-inner,
  .uppy-Dashboard-innerWrap,
  .uppy-Dashboard-AddFiles {
    border-radius: 1rem;
  }

  .uppy-Dashboard {
    height: 100cqh;
  }
  .uppy-Dashboard-poweredBy {
    display: none !important;
  }

  .uppy-Dashboard-inner {
    background: #ffffff;
  }

  .uppy-Dashboard-AddFiles {
    border: 1px solid #e6e6e6;
  }

  .uppy-Dashboard-AddFiles-list {
    [data-uppy-acquirer-id="MyDevice"] {
      display: none;
    }
  }
}

.textarea {
  height: 100cqh;
  border-radius: 1rem;
  resize: none;
  border: 1px solid #e6e6e6;
}

.grading-buttons-row {
  display: flex;
  gap: 1rem;
  padding-bottom: 1rem;
  flex-wrap: wrap;
}

.logo {
  width: 30px;
  height: 20px;
  margin-top: -3px;
}

.grade-button {
  width: 100%;
}

button.btn.use-sample {
  background: #6a50f3;
  color: #fff;
}

.form-control:focus {
  box-shadow: none;
  border-color: #e2e2e2;
}

.dropdown::v-deep .btn,
.btn {
  border-radius: 0.5rem;
}

.btn-secondary:not(:disabled):not(.disabled).active:focus {
  box-shadow: none;
}
</style>
